import { useEffect, useMemo, useState } from 'react'
import { Check, Search } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import {
  useAssignProducts,
  useUnassignedProducts,
} from '../hooks/use-categories'
import { assignableTargets } from '../lib/build-tree'
import type { Category, UnassignedFilter, UnassignedProduct } from '@/api/types'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

const PAGE_SIZE = 50

function formatCents(cents: number, locale: string) {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(cents / 100)
}

interface UnassignedTableProps {
  categories: Array<Category>
}

/**
 * The assignment worklist: articles in the collector category that were sold in the period or at
 * least carry a price.
 *
 * One table rather than two sections, split by a separator row. Production holds ~5.000 articles of
 * which roughly a hundred are in use, so this list is paged — and paging across two separate
 * arrays cannot work, which is why the API returns a single ordered list with a `sold` flag.
 */
export function UnassignedTable({ categories }: UnassignedTableProps) {
  const { t: tCategories, i18n } = useTranslation('categories')
  const locale = i18n.language || 'de-AT'

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [filter, setFilter] = useState<UnassignedFilter>('all')
  const [days, setDays] = useState('30')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [targetCategoryId, setTargetCategoryId] = useState<string>('')

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  // Any change of scope invalidates the page number and the selection: a selected SKU that is no
  // longer listed must not be assigned by a later click.
  useEffect(() => {
    setPage(1)
    setSelected(new Set())
  }, [debouncedSearch, filter, days])

  const from = useMemo(
    () =>
      new Date(Date.now() - Number(days) * 24 * 60 * 60 * 1000).toISOString(),
    [days],
  )

  const { data, isLoading } = useUnassignedProducts({
    from,
    q: debouncedSearch || undefined,
    filter,
    page,
    pageSize: PAGE_SIZE,
  })

  const assign = useAssignProducts()

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const soldCount = data?.soldCount ?? 0
  const pricedCount = data?.pricedCount ?? 0
  // The rows are filtered by the active chip, so paging has to count the same subset — total counts
  // the whole scope and would page into empty pages once a chip is active. The three counts already
  // respect the search box, so the matching one is exactly the filtered row count.
  const filteredTotal =
    filter === 'sold' ? soldCount : filter === 'priced' ? pricedCount : total
  const pageCount = Math.max(1, Math.ceil(filteredTotal / PAGE_SIZE))

  // Tree order, collector excluded — see assignableTargets.
  const assignable = useMemo(() => assignableTargets(categories), [categories])

  const toggle = (sku: string) => {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(sku)) next.delete(sku)
      else next.add(sku)
      return next
    })
  }

  const selectAllSold = () => {
    setSelected(new Set(items.filter((i) => i.sold).map((i) => i.sku)))
  }

  const runAssign = () => {
    const id = Number(targetCategoryId)
    if (!id || selected.size === 0) return
    assign.mutate(
      { id, skus: Array.from(selected) },
      {
        onSuccess: () => {
          setSelected(new Set())
          setTargetCategoryId('')
        },
      },
    )
  }

  // Where the sold rows end and the merely priced ones begin — the server orders them that way, so
  // the boundary is wherever the first unsold row appears on this page.
  const separatorIndex = items.findIndex((i) => !i.sold)

  // placeholderData keeps the previous page on screen, so isLoading is only true on first load.
  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder={tCategories('unassigned.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-1 rounded-lg bg-muted p-1">
          {(
            [
              ['all', tCategories('unassigned.filterAll'), total],
              ['sold', tCategories('unassigned.filterSold'), soldCount],
              ['priced', tCategories('unassigned.filterPriced'), pricedCount],
            ] as Array<[UnassignedFilter, string, number]>
          ).map(([value, label, count]) => (
            <Button
              key={value}
              variant={filter === value ? 'outline' : 'ghost'}
              size="sm"
              className={cn(filter === value && 'bg-background shadow-xs')}
              onClick={() => setFilter(value)}
            >
              {label}
              <span className="ml-1.5 tabular-nums text-muted-foreground">
                {count}
              </span>
            </Button>
          ))}
        </div>

        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="30">
              {tCategories('unassigned.days30')}
            </SelectItem>
            <SelectItem value="90">
              {tCategories('unassigned.days90')}
            </SelectItem>
            <SelectItem value="365">
              {tCategories('unassigned.days365')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk bar */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-primary px-4 py-3 text-primary-foreground">
          <div className="flex items-center gap-3 text-sm">
            <Check className="h-4 w-4" />
            <span>
              {tCategories('unassigned.selected', { count: selected.size })}
            </span>
            <button
              type="button"
              className="underline underline-offset-2 opacity-80"
              onClick={selectAllSold}
            >
              {tCategories('unassigned.selectAllSold', { count: soldCount })}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Select
              value={targetCategoryId}
              onValueChange={setTargetCategoryId}
            >
              {/* The indent marker belongs to the list, not to the trigger: Radix renders the
                  selected item's content in both, and a lone "›" in front of the chosen group
                  reads as a stray character. */}
              <SelectTrigger className="h-9 w-64 bg-background text-foreground [&_[data-tree-indent]]:hidden">
                <SelectValue
                  placeholder={tCategories('unassigned.assignPlaceholder')}
                />
              </SelectTrigger>
              <SelectContent>
                {assignable.map(({ category, depth }) => (
                  <SelectItem key={category.id} value={String(category.id)}>
                    {depth > 0 && (
                      <span
                        data-tree-indent
                        aria-hidden
                        className="text-muted-foreground"
                        style={{ marginLeft: `${(depth - 1) * 12}px` }}
                      >
                        ›
                      </span>
                    )}
                    <span>{category.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="secondary"
              size="sm"
              disabled={!targetCategoryId || assign.isPending}
              onClick={runAssign}
            >
              {tCategories('unassigned.assign')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-primary-foreground/70"
              onClick={() => setSelected(new Set())}
            >
              {tCategories('unassigned.clearSelection')}
            </Button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="rounded-lg border py-12 text-center text-muted-foreground">
          {debouncedSearch
            ? tCategories('unassigned.noMatches')
            : tCategories('unassigned.allAssigned')}
        </div>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10" />
                <TableHead>{tCategories('unassigned.article')}</TableHead>
                <TableHead>{tCategories('table.code')}</TableHead>
                <TableHead className="text-right">
                  {tCategories('unassigned.price')}
                </TableHead>
                <TableHead className="text-right">
                  {tCategories('unassigned.revenue')}
                </TableHead>
                <TableHead className="text-right">
                  {tCategories('unassigned.units')}
                </TableHead>
                <TableHead className="text-right">
                  {tCategories('unassigned.lastSold')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item, index) => (
                <UnassignedRow
                  key={item.sku}
                  item={item}
                  locale={locale}
                  checked={selected.has(item.sku)}
                  onToggle={() => toggle(item.sku)}
                  showSeparatorBefore={index === separatorIndex && index > 0}
                  separatorLabel={tCategories('unassigned.separator', {
                    count: pricedCount,
                  })}
                  neverLabel={tCategories('unassigned.never')}
                />
              ))}
            </TableBody>
          </Table>

          <div className="flex items-center justify-between gap-4 border-t px-4 py-3">
            <span className="text-sm tabular-nums text-muted-foreground">
              {tCategories('unassigned.pageInfo', {
                shown: items.length,
                total: filteredTotal,
              })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {tCategories('unassigned.previous')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pageCount}
                onClick={() => setPage((p) => p + 1)}
              >
                {tCategories('unassigned.next')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

interface UnassignedRowProps {
  item: UnassignedProduct
  locale: string
  checked: boolean
  onToggle: () => void
  showSeparatorBefore: boolean
  separatorLabel: string
  neverLabel: string
}

function UnassignedRow({
  item,
  locale,
  checked,
  onToggle,
  showSeparatorBefore,
  separatorLabel,
  neverLabel,
}: UnassignedRowProps) {
  return (
    <>
      {showSeparatorBefore && (
        <TableRow className="hover:bg-muted">
          <TableCell
            colSpan={7}
            className="bg-muted py-2 text-center text-xs font-medium text-muted-foreground"
          >
            {separatorLabel}
          </TableCell>
        </TableRow>
      )}
      <TableRow className={cn(checked && 'bg-muted/50')}>
        <TableCell>
          <Checkbox checked={checked} onCheckedChange={onToggle} />
        </TableCell>
        <TableCell
          className={cn(item.sold ? 'font-medium' : 'text-muted-foreground')}
        >
          {item.name}
        </TableCell>
        <TableCell className="font-mono text-sm text-muted-foreground">
          {item.sku}
        </TableCell>
        <TableCell className="text-right tabular-nums">
          {item.priceCents == null ? '–' : formatCents(item.priceCents, locale)}
        </TableCell>
        <TableCell
          className={cn(
            'text-right tabular-nums',
            item.sold ? 'font-semibold' : 'text-muted-foreground',
          )}
        >
          {formatCents(item.revenueCents, locale)}
        </TableCell>
        <TableCell className="text-right tabular-nums">
          {item.units || '–'}
        </TableCell>
        <TableCell className="text-right tabular-nums text-muted-foreground">
          {item.lastSoldAt
            ? new Date(item.lastSoldAt).toLocaleDateString(locale)
            : neverLabel}
        </TableCell>
      </TableRow>
    </>
  )
}
